import { defineStore } from 'pinia';
import { computed, reactive, ref, toRefs } from 'vue';
import { sceneState, type SceneState } from '@/lib/threeScene/SceneContext';
import {
    catstackOverlayNodes,
    type CatstackOverlayNodes,
    centerCameraNodeOnSceneCentre
} from '@/views/dicoms/overlay/scene/catstackOverlayNodes';
import { type CatstackOverlayNode, OverlaySceneContext } from '@/views/dicoms/overlay/scene/overlaySceneContext';
import { DataState, withStateLoading } from '@/util/dataState';
import { type OverlayState, overlayState, resetCatstackOverlayState } from './overlayState';
import { loadData } from './loadCatstackOverlayData';
import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';

export type OverlayStore = OverlayState & {
    state: DataState;
    isLoading: boolean;

    coronalNodes: CatstackOverlayNodes;
    sagittalNodes: CatstackOverlayNodes;
    axialNodes: CatstackOverlayNodes;

    coronalSceneState: SceneState<CatstackOverlayNode>;
    sagittalSceneState: SceneState<CatstackOverlayNode>;
    axialSceneState: SceneState<CatstackOverlayNode>;

    setSelectedView: (view: CatstackPlaneId) => void;
    loadCase: (caseId: number) => Promise<void>;

    getNodesForView: (view: CatstackPlaneId) => CatstackOverlayNodes;
    getSceneStateForView: (view: CatstackPlaneId) => SceneState<CatstackOverlayNode>;
    setSceneContextForView: (view: CatstackPlaneId, context: OverlaySceneContext) => void;
    getSceneContextForView: (view: CatstackPlaneId) => OverlaySceneContext;
};

export const useOverlayStore: () => OverlayStore = defineStore('catstack-overlay', () => {
    const state = reactive(overlayState());

    // Create separate nodes and scene states for each view with unique IDs
    const coronalNodes = ref<CatstackOverlayNodes>(catstackOverlayNodes('coronal'));
    const sagittalNodes = ref<CatstackOverlayNodes>(catstackOverlayNodes('sagittal'));
    const axialNodes = ref<CatstackOverlayNodes>(catstackOverlayNodes('axial'));

    const coronalSceneState = ref<SceneState<CatstackOverlayNode>>(sceneState());
    const sagittalSceneState = ref<SceneState<CatstackOverlayNode>>(sceneState());
    const axialSceneState = ref<SceneState<CatstackOverlayNode>>(sceneState());

    // Store scene contexts
    const sceneContexts = new Map<CatstackPlaneId, any>();

    const isLoading = computed(() => state.state === DataState.Loading);

    const getNodesForView = (view: CatstackPlaneId): CatstackOverlayNodes => {
        switch (view) {
            case 'coronal':
                return coronalNodes.value;
            case 'sagittal':
                return sagittalNodes.value;
            case 'axial':
                return axialNodes.value;
        }
    };

    const getSceneStateForView = (view: CatstackPlaneId): SceneState<CatstackOverlayNode> => {
        switch (view) {
            case 'coronal':
                return coronalSceneState.value;
            case 'sagittal':
                return sagittalSceneState.value;
            case 'axial':
                return axialSceneState.value;
        }
    };

    const loadCase = async (caseId: number): Promise<void> => {
        try {
            await withStateLoading(state, async () => {
                resetCatstackOverlayState(state);

                sceneContexts.clear();

                coronalNodes.value = catstackOverlayNodes('coronal');
                sagittalNodes.value = catstackOverlayNodes('sagittal');
                axialNodes.value = catstackOverlayNodes('axial');

                coronalSceneState.value = sceneState();
                sagittalSceneState.value = sceneState();
                axialSceneState.value = sceneState();

                const nodesSets = {
                    coronalNodes: coronalNodes.value,
                    sagittalNodes: sagittalNodes.value,
                    axialNodes: axialNodes.value,
                };
                await loadData(state, nodesSets, caseId);

                if (state.sceneCentre && state.case?.operativeSide) {
                    centerCameraNodeOnSceneCentre(
                        coronalNodes.value.camera,
                        state.sceneCentre,
                        'coronal',
                        state.case.operativeSide
                    );
                    centerCameraNodeOnSceneCentre(
                        sagittalNodes.value.camera,
                        state.sceneCentre,
                        'sagittal',
                        state.case.operativeSide
                    );
                    centerCameraNodeOnSceneCentre(
                        axialNodes.value.camera,
                        state.sceneCentre,
                        'axial',
                        state.case.operativeSide
                    );
                }
            });

        } catch (error) {
            console.error('Failed to load overlay data:', error);
            throw error;
        }
    };

    const setSelectedView = (view: CatstackPlaneId) => {
        state.catstackMode = view;
    };

    const setSceneContextForView = (view: CatstackPlaneId, context: any) => {
        sceneContexts.set(view, context);
    };

    const getSceneContextForView = (view: CatstackPlaneId) => {
        return sceneContexts.get(view);
    };

    return {
        ...toRefs(state),
        isLoading,

        coronalNodes: coronalNodes,
        sagittalNodes: sagittalNodes,
        axialNodes: axialNodes,
        coronalSceneState: coronalSceneState,
        sagittalSceneState: sagittalSceneState,
        axialSceneState: axialSceneState,

        setSelectedView,
        loadCase,
        getNodesForView,
        getSceneStateForView,
        setSceneContextForView,
        getSceneContextForView,
    };
});
